import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import LiveQueue from "../components/LiveQueue";
import Loader from "../components/Loader";
import { useUserData } from "../lib/contexts/UserContext";
import { useHospDocData } from "../lib/contexts/HospitalDoctorContext";
import { useInterval } from "../lib/utils/useInterval";
import { useNavigate } from "react-router-dom";
import ScrollingText from "../components/ScrollingText";
import { getResourcesByHospitalId } from "../lib/apis/resources";
import { ResourcesType } from "../lib/utils/types";
import { HiArrowLeft, HiOutlineHome } from "react-icons/hi2";
import Marquee from "react-fast-marquee";

// `category` is a free-form string, so a strict === "video" check sent values
// like "Video" or "video/mp4" down the <img> branch, rendering a video URL as a
// broken image that never advanced.
const isVideoRes = (r?: ResourcesType) =>
  !!r?.category?.toLowerCase().includes("video");

const ColumnView = () => {
  const { userData } = useUserData();
  const { hospitalID, doctors } = useHospDocData();
  const navigate = useNavigate();

  const [, setTime] = useState(moment().format("hh:mm:ss A"));
  const [resources, setResources] = useState<ResourcesType[]>([]);
  const [currentResourceIndex, setCurrentResourceIndex] = useState<number>(0);
  const [showResource, setShowResource] = useState<boolean>(false); // Controls whether the resource is visible
  const [isResourcePlaying, setIsResourcePlaying] = useState<boolean>(false); // To track if resource is currently playing

  // ✅ Persist toggle state using localStorage
  const [isToggled] = useState<boolean>(() => {
    return localStorage.getItem("adsToggle") === "true";
  });

  // Fetch resources from API
  const fetchResources = async () => {
    try {
      const response = await getResourcesByHospitalId(String(hospitalID));
      if (response.data.status === "SUCCESS") {
        setResources(response.data.result);
      } else {
        console.error("Failed to fetch resources.");
      }
    } catch (error) {
      console.error("Error fetching resources:", error);
    }
  };

  // Fetch resources on component mount
  useEffect(() => {
    if (hospitalID) {
      fetchResources();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitalID]);

  // Update time every second
  useInterval(() => {
    setTime(moment().format("hh:mm:ss A"));
  }, 1000);

  // A resource that never plays used to latch this screen: the overlay is
  // fullscreen and the queue behind it is hidden, so the whole TV went black
  // until someone reloaded it. skip() closes the overlay and steps past the bad
  // resource; strikesRef stops us cycling forever when every resource is broken.
  const strikesRef = useRef(0);
  const [adsDead, setAdsDead] = useState(false);

  const skip = (reason: string) => {
    console.warn(
      "[tv-ads] skipping resource:",
      reason,
      resources[currentResourceIndex]?.resource_url
    );
    // Always uncover the queue — a visible queue beats a black screen.
    setShowResource(false);
    setIsResourcePlaying(false);
    strikesRef.current += 1;
    if (strikesRef.current >= resources.length) {
      setAdsDead(true); // every resource failed; stop showing ads entirely
      return;
    }
    setCurrentResourceIndex((prev) => (prev + 1) % resources.length);
  };

  // Handle the transition between column view and resource view
  useEffect(() => {
    if (resources.length > 0 && isToggled && !adsDead) {
      if (!showResource) {
        // Initially, the column view is shown for 2 minutes
        const timer = setTimeout(() => {
          setShowResource(true); // Show the resource after 2 minutes
        }, 120000);
        return () => clearTimeout(timer);
      }

      if (isResourcePlaying) {
        setShowResource(false);
        setCurrentResourceIndex(
          (prevIndex) => (prevIndex + 1) % resources.length
        );
        setIsResourcePlaying(false);
      }
    }
  }, [showResource, isResourcePlaying, resources, isToggled, adsDead]);

  // Last resort. onError covers a resource that fails, but a request that simply
  // hangs (weak clinic wifi, captive portal) fires NO event at all — not error,
  // not ended — which is what leaves the overlay covering the queue forever. If
  // nothing has started playing 30s after the overlay opened, give up on it.
  const startedRef = useRef(false);
  useEffect(() => {
    startedRef.current = false;
  }, [showResource, currentResourceIndex]);

  useEffect(() => {
    if (!showResource) return;
    const t = setTimeout(() => {
      if (!startedRef.current) skip("nothing played within 30s");
    }, 30000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResource, currentResourceIndex]);

  useEffect(() => {
    if (doctors === undefined) navigate("/" + hospitalID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctors]);

  const handleResourceEnd = () => {
    setIsResourcePlaying(true);
  };

  const handleImageEnd = () => {
    setIsResourcePlaying(true);
  };

  return (
    <>
      <div className="fixed py-1 bg-[#0F3CBE] z-[999] w-[100vw]">
        <Marquee className="text-white " direction="right" speed={100}>
          Please check in upon entering the clinic
        </Marquee>
      </div>
      {userData !== undefined ? (
        <div className="relative h-[100vh]">
          {/* Column View Section (always rendered in the background) */}
          <div className={`${showResource ? "hidden" : "block"} flex flex-col h-screen pt-9`}>
            <div className="flex flex-row justify-between items-center pt-1 pb-2 px-6 flex-none">
              <div className="flex gap-3 items-center">
                <div
                  className="bg-white p-1.5 rounded-full shadow-md cursor-pointer
                   hover:bg-[#0F3CBE] hover:text-white transition-all duration-200"
                  onClick={() => navigate("/" + hospitalID + "/choose-doctors")}
                >
                <HiArrowLeft
                  className="w-4 h-4 text-[#0F3CBE]"
                  style={{ color: 'inherit' }} // This ensures the 'hover:text-white' from parent works
                />
                </div>
                <div
                  className="bg-white p-1.5 rounded-full shadow-md cursor-pointer
                   hover:bg-[#0F3CBE] hover:text-white transition-all duration-200"
                  onClick={() => navigate("/" + hospitalID)}
                >
                <HiOutlineHome
                  className="w-4 h-4 text-[#0F3CBE] hover:!text-white"
                  style={{ color: 'inherit' }} // This ensures the 'hover:text-white' from parent works
                />
                </div>
                {/* <p className="flex  text-2xl ">{time}</p> */}
              </div>
              {/* <button
                onClick={() => navigate("/admin")}
                className="w-[8%] mr-10"
              >
                <img
                  src={require("../assets/images/DTlogo.png")}
                  alt="Queue empty"
                  className="flex w-[8rem] h-[3rem] cursor-pointer z-40"
                  onClick={() => navigate("/admin")}
                />
              </button> */}
            </div>

            {/* Live Queue Display */}
            <div
              className={`grid grid-cols-1 flex-1 overflow-hidden pb-2 ${
                doctors?.length === 2 ? "grid-cols-2" : "lg:grid-cols-3"
              } gap-5 mx-[12px]`}
            >
              {doctors?.map((doc, index) => (
                <LiveQueue key={index} mapping_id={doc.mapping_id} />
              ))}
            </div>

            {/* Scrolling text if needed */}
            {hospitalID === "3635f86c-6baf-4fbc-99b1-b47d555afce0" && (
              <ScrollingText />
            )}
          </div>

          {/* Resource (Ad) Overlay: displayed on top of the column view when showResource is true */}
          {showResource && (
            <div className="absolute top-0 left-0 w-full h-[100vh] overflow-hidden flex justify-center items-center bg-black bg-opacity-70 z-50">
              <div className="flex flex-col justify-center items-center text-white w-full h-[100vh]">
                {resources.length > 0 ? (
                  isVideoRes(resources[currentResourceIndex]) ? (
                    <video
                      src={resources[currentResourceIndex]?.resource_url}
                      autoPlay
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                      onPlaying={() => {
                        strikesRef.current = 0;
                        startedRef.current = true;
                      }}
                      onEnded={handleResourceEnd} // Trigger resource end when video finishes
                      onError={() => skip("video failed to load")}
                    />
                  ) : (
                    <img
                      src={resources[currentResourceIndex]?.resource_url}
                      alt={`Resource ${resources[currentResourceIndex]?.resource_id}`}
                      className="w-full h-full object-cover"
                      onLoad={() => {
                        strikesRef.current = 0;
                        startedRef.current = true;
                        setTimeout(handleImageEnd, 10000);
                      }} // Show image for 10 seconds
                      onError={() => skip("image failed to load")}
                    />
                  )
                ) : (
                  <video
                    src="https://ik.imagekit.io/gn9by35ed/Button%20(Landscape)%20(1).webm/ik-video.mp4?updatedAt=1734682888467"
                    autoPlay
                    loop
                    muted
                  />
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <Loader />
      )}
    </>
  );
};

export default ColumnView;
